<template>
  <div v-if="!pending" class="tailwind">
    <ErrorModal />
    <MaxStayLengthErrorModal />
    <PriceCalendar :config="sbConfig" />
    <AvailabilityCalendar :config="sbConfig" />
    <SimpleCalendar :config="sbConfig" />
    <ModifyDatesCalendar />

    <template v-if="isWebextraPage">
      <template v-if="isWebextraOveriviewPage">
        <!-- CT MenuWebExtra: only overview (dynamic button / sticky menu) -->
        <MenuDynamicButton :config="sbConfig" />
      </template>
      <template v-else-if="isWebextraMeetingEventsPage">
        <!-- CT MenuWebExtraWithoutBookNow: meetings (no button / sticky menu) -->
        <!-- CT MenuWebExtra: my account (no button / no sticky menu) -->
        <MenuNoButton :sticky="searchbarStickyBehavior === 'sticky'" :config="sbConfig" />
      </template>
      <template v-else>
        <!-- CT MenuWebExtraFixedBookNow: only property pages excepts overview (fixed button / sticky menu) -->
        <MenuFixedButton :config="sbConfig" />
      </template>
    </template>
    <template v-else-if="isBrandCorner">
      <div ref="sbBrandContainer">
        <BrandSearchbar ref="sbBrand" :config="sbConfig" />
      </div>
    </template>
    <template v-else>
      <div ref="sbHomeContainer" v-if="searchBarType === SearchBarTypes.AUTOCOMPLETE">
        <HomeSearchbar
          ref="sbHome"
          :config="sbConfig"
          :customer="customer"
          :flexibleDatesEnabled="flexibleDatesEnabled"
        />
      </div>
      <div
        ref="sbDiscreet"
        v-if="
          searchBarType === SearchBarTypes.ROOM_DISPLAY ||
          searchBarType === SearchBarTypes.SEARCH_RESULTS ||
          searchBarType === SearchBarTypes.REWARDS
        "
      >
        <DiscreetSearchbar
          :config="sbConfig"
          :customer="customer"
          :enableR4uBehavior="searchBarType !== SearchBarTypes.REWARDS"
          :flexibleDatesEnabled="
            searchBarType !== SearchBarTypes.ROOM_DISPLAY ? flexibleDatesEnabled : false
          "
        />
      </div>
      <div
        ref="sbOffers"
        v-if="searchBarType === SearchBarTypes.OFFERS && !isSingleHotelOfferPage"
        class="searchbar--offers"
      >
        <OfferSearchbar :pacCode="pacCode" :config="sbConfig" :minimumNights="minimumNights" />
      </div>
      <div
        ref="sbHotelList"
        v-if="searchBarType === SearchBarTypes.HOTEL_LIST && hotelListAutomated"
        class="searchbar--hotelList"
      >
        <HotelListSearchBar :config="sbConfig" :filters="filters" :noResultsMsg="noResultsMsg" />
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, watch, computed } from 'vue'
import '@common/lib-vue-common/styles-legacy.css'
import {
  HomeSearchbar,
  BrandSearchbar,
  DiscreetSearchbar,
  OfferSearchbar,
  HotelListSearchBar,
  ErrorModal,
  PriceCalendar,
  MaxStayLengthErrorModal,
  AvailabilityCalendar,
  SimpleCalendar,
  ModifyDatesCalendar,
  useGetParams,
  useStackModal,
  useDaysBetween
} from '@common/lib-vue-common'
import { startUpLabels } from './composables/useLabels.js'
import { SearchBarTypes, maxStayLength } from './infrastructure/Configuration'
import { createCities, createCountry } from './composables/useSearchbar'
import MenuDynamicButton from './searchbar/MenuDynamicButton.vue'
import MenuFixedButton from './searchbar/MenuFixedButton.vue'
import MenuNoButton from './searchbar/MenuNoButton.vue'
import { isEnabled } from './isEnabled.js'

const pending = ref(true)
const searchBarType = ref()
const pacCode = ref()
const sbConfig = ref()
const minimumNights = ref(1)
const sbHomeContainer = ref(null)
const sbBrandContainer = ref(null)
const sbHome = ref()
const sbBrand = ref()
const sbDiscreet = ref(null)
const sbOffers = ref(null)
const sbHotelList = ref(null)
//const sbPosition = ref('initial')

const isWebextraPage = ref(false)
const isWebextraOveriviewPage = ref(false)
const isWebextraMeetingEventsPage = ref(false)
const isSingleHotelOfferPage = ref(false)
const isHotelListPage = ref(false)
const isBrandCorner = ref(false)

const searchbarStickyBehavior = ref()
const searchbarBookButtonBehavior = ref()
const hotelListAutomated = ref()
const noResultsMsg = ref('')

const filters = ref({})
const customer = ref({})

const setSbSticky = async sbRef => {
  await nextTick(() => {
    sbRef.value.parentNode.parentNode.style.position = 'sticky'
    sbRef.value.parentNode.parentNode.style.top = '0'
    sbRef.value.parentNode.parentNode.style.zIndex = '2'
  })
}

const flexibleDatesEnabled = computed(() => isEnabled('booking.sr.flexible.dates.avail', false))

//const { isXs, isSm } = useScreen('xs', 'sm', 'md', 'lg', 'xl', '2xl');
//const isMobile = computed(() => isXs.value || isSm.value);
//const getStickyOffset = () => isMobile ? 65 : 90

onMounted(async () => {
  isWebextraPage.value = window?.appGlobalProps?.isWebextraPage || false
  isWebextraOveriviewPage.value = window?.appGlobalProps?.isWebextraOveriviewPage || false
  isWebextraMeetingEventsPage.value = window?.appGlobalProps?.isWebextraMeetingEventsPage || false
  isSingleHotelOfferPage.value = window?.appGlobalProps?.isSingleHotelOfferPage || false
  isHotelListPage.value = window?.appGlobalProps?.isHotelListPage || false

  isBrandCorner.value = window?.appGlobalProps?.isBrandCorner || false

  searchBarType.value = window?.appGlobalProps?.searchBarType || 'autocomplete'
  pacCode.value = window?.appGlobalProps?.offersData?.pacCode || ''
  minimumNights.value = window?.appGlobalProps?.offersData?.minimumNights

  searchbarStickyBehavior.value = window?.appGlobalProps?.searchbarStickyBehavior
  searchbarBookButtonBehavior.value = window?.appGlobalProps?.searchbarBookButtonBehavior

  hotelListAutomated.value = window?.hotelListAutomated

  $(window).on('auth.logged auth.postlogged', function (event, data) {
    customer.value = { ...data, isLogged: true }
  })
  $(window).on('auth.nologged', function () {
    customer.value = { isLogged: false }
  })

  if (hotelListAutomated.value && hotelListAutomated.value.length) {
    const hotelListAutomatedInfo =
      hotelListAutomated.value.find(hotelList => hotelList.type === 'MAP-LIST') ||
      hotelListAutomated.value[0]

    if (hotelListAutomatedInfo && hotelListAutomatedInfo.filters) {
      noResultsMsg.value = hotelListAutomatedInfo.noResultsMessage?.text || ''

      filters.value = {
        brand: hotelListAutomatedInfo.filters.brands,
        region: hotelListAutomatedInfo.filters.regions,
        country: hotelListAutomatedInfo.filters.countries,
        city: hotelListAutomatedInfo.filters.cities,
        status: hotelListAutomatedInfo.filters.status,
        type: hotelListAutomatedInfo.filters.types,
        service: hotelListAutomatedInfo.filters.services,
        locationType: hotelListAutomatedInfo.filters.locationTypes,
        accessibility: hotelListAutomatedInfo.filters.accessibilities,
        code: hotelListAutomatedInfo.filters.codes,
        stars: hotelListAutomatedInfo.filters.stars,
        limit: hotelListAutomatedInfo.filters.limit
      }
    }
  } else if (searchBarType.value === SearchBarTypes.HOTEL_LIST) {
    const searchBar = document.getElementById('searchbar')
    if (searchBar) {
      searchBar.style.display = 'none'
    }
  }

  const mappedDestinationsData = {
    ...window?.appGlobalProps?.offersData,
    countries: (window?.appGlobalProps?.offersData?.countries || []).flatMap(c =>
      createCountry(c.name, c.cities, c.code)
    ),
    cities: (window?.appGlobalProps?.offersData?.countries || []).flatMap(c =>
      createCities(c.name, c.cities, c.code)
    )
  }

  sbConfig.value = {
    allowedRateTypes: window?.appGlobalProps?.specialRates || [],
    destinationsData: mappedDestinationsData || null
  }
  await startUpLabels()
  pending.value = false

  nextTick(() => isMaxStayLengthErrorModal())
  /*window?.addEventListener('scroll', () => {
    if (!sbHomeContainer.value) return

    const { y } = sbHomeContainer.value.getBoundingClientRect();
    if (y === 0 && sbPosition.value === 'initial') {
      sbPosition.value = 'sticky'
      sbHome.value?.collapse()
    } else if (y >= getStickyOffset() && sbPosition.value === 'sticky') {
      sbPosition.value = 'initial'
      sbHome.value?.expand()
    }
  })*/
})

const isMaxStayLengthErrorModal = () => {
  try {
    const { checkInDate, checkOutDate } = useGetParams('checkInDate', 'checkOutDate')
    if (checkInDate !== null && checkOutDate !== null) {
      if (useDaysBetween(checkInDate, checkOutDate) > maxStayLength) {
        useStackModal('max-stay-length-error-modal')
      }
    }
  } catch (e) {
    console.error(e)
  }
}

watch(
  sbHotelList,
  newValue => {
    if (newValue) {
      setSbSticky(sbHotelList)
    }
  },
  { immediate: true, deep: true }
)

watch(
  sbOffers,
  newValue => {
    if (newValue) {
      setSbSticky(sbOffers)
    }
  },
  { immediate: true, deep: true }
)

watch(
  sbDiscreet,
  newValue => {
    if (newValue) {
      setSbSticky(sbDiscreet)
    }
  },
  { immediate: true, deep: true }
)

watch(
  sbHomeContainer,
  newValue => {
    if (newValue) {
      setSbSticky(sbHomeContainer)
    }
  },
  { immediate: true, deep: true }
)

watch(
  sbBrandContainer,
  newValue => {
    if (newValue) {
      setSbSticky(sbBrandContainer)
    }
  },
  { immediate: true, deep: true }
)
</script>

<style lang="css" scoped>
.searchbar {
  &--offers,
  &--hotelList {
    position: sticky;
    top: 0;
    z-index: 1;
  }
}
</style>
